<template>
  <div class="plotter-header" :layout="layout">
    <section class="meta">
      <div class="plotter-header__mode-switcher">
        <a
          v-if="!(isEmbedded || isPrintable)"
          class="plotter-header__mode-switcher-btn"
          @click="switchMode"
        >
          Exit Compare
        </a>
        <a
          v-if="!(isEmbedded || isPrintable)"
          class="plotter-header__reset-chart-btn"
          @click="resetChart"
          >Reset</a
        >
      </div>
    </section>
    <section class="main">
      <flip-selector
        v-if="flipDimensions.length"
        :disabled="mainBreakdownDimensions.length == 0"
        :dimensions="flipDimensions"
      />
      <indicator-selector
        v-if="isExploreLayout || mainIndId !== null"
        v-model="openSelector"
        :app-slot="'main'"
        :breakdown-selection="mainBreakdownSelection"
        :breakdown-dimensions="mainBreakdownDimensions"
        :units="units.main"
        :editable="isExploreLayout"
        :is-loading="isMainLoading"
        :required="true"
        :class="{ faded: openSelector && openSelector !== 'main' }"
        @update="updateSelection('breakdowns', $event)"
      />
      <indicator-selector
        v-if="isExploreLayout || pairIndId !== null"
        v-model="openSelector"
        :app-slot="'pair'"
        :breakdown-selection="pairBreakdownSelection"
        :breakdown-dimensions="pairBreakdownDimensions"
        :units="units.pair"
        :editable="isExploreLayout"
        :is-loading="isPairLoading"
        :required="true"
        :class="{ faded: openSelector && openSelector !== 'pair' }"
        @update="updateSelection('breakdowns', $event)"
      />
      <indicator-selector
        v-if="isExploreLayout || sizeIndId !== null"
        v-model="openSelector"
        :app-slot="'size'"
        :breakdown-selection="sizeBreakdownSelection"
        :breakdown-dimensions="sizeBreakdownDimensions"
        :unit="units.size"
        :editable="isExploreLayout"
        :is-loading="isSizeLoading"
        :class="{ faded: openSelector && openSelector !== 'size' }"
        @update="updateSelection('breakdowns', $event)"
      />
      <div v-if="!(isPrintable || isThumbnail)" class="plotter-header__country-selectors">
        <country-selector
          :selection="countrySelection"
          :countries="dataCountries"
          @update="updateSelection('countries', $event)"
        />
        <div class="plotter-header__country-filter">
          <switcher-selector v-model="countryFilter" :disabled="countrySelection.length == 0" />
          <label :fade-out="countrySelection.length === 0">{{ hideUnselectedLabel }}</label>
        </div>
        <div class="plotter-header__country-filter show-full-label">
          <switcher-selector v-model="fullLabels" />
          <label>{{ $lng('show-full-labels') }}</label>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import CountrySelector from '../selectors/CountrySelector.vue'
import IndicatorSelector from '../selectors/IndicatorSelector.vue'
import SwitcherSelector from '../selectors/SwitcherSelector.vue'
import FlipSelector from '../selectors/FlipSelector.vue'
import { filterChartRowByTimeScale } from '@/utils/dates'
import { mergeTimes } from '@/utils/query'

export default {
  components: { CountrySelector, IndicatorSelector, SwitcherSelector, FlipSelector },
  props: [
    'dataCountries',
    'countrySelection',
    'mainBreakdownSelection',
    'mainBreakdownDimensions',
    'pairBreakdownSelection',
    'pairBreakdownDimensions',
    'sizeBreakdownSelection',
    'sizeBreakdownDimensions',
    'flipDimensions',
    'units',
  ],
  data() {
    return {
      openSelector: null,
    }
  },
  computed: {
    mainIndId() {
      return this.$store.state.query.mainIndId
    },
    pairIndId() {
      return this.$store.state.query.pairIndId
    },
    sizeIndId() {
      return this.$store.state.query.sizeIndId
    },
    cubeName() {
      return this.$store.getters.cubeName('main')
    },
    countryFilter: {
      get() {
        return this.$store.state.query.countryFilter
      },
      set(to) {
        this.$store.commit('countryFilter', to)
      },
    },
    countryDimension() {
      return this.$store.getters.countryDimension('main')
    },
    isFlipped() {
      return this.$store.state.query.flip != false
    },
    hideUnselectedLabel() {
      const dimension = this.$store.getters.mainCountryDimension
      const dimName = this.$lng(dimension.name[this.lng]).toLowerCase()
      return `Show selected ${dimName} only`
    },
    fullLabels: {
      get() {
        return this.$store.state.query.fullLabel
      },
      set(to) {
        this.$store.commit('fullLabel', to)
      },
    },
    isMainLoading() {
      return this.$store.getters.mainConstraints === void 0 && false
    },
    isPairLoading() {
      return this.$store.getters.pairConstraints === void 0 && false
    },
    isSizeLoading() {
      return this.$store.getters.sizeConstraints === void 0 && false
    },
  },
  methods: {
    resetChart() {
      const getSlotDates = slot => {
        if (this.$store.state.query[slot + 'CubeId'] === null) return []
        return this.$store.getters[slot + 'AvailableDates']
          .filter(date => {
            return filterChartRowByTimeScale(date, this.$store.state.query.timeScale)
          })
          .map(date => date.getTime())
      }

      const dataDates = ['main', 'pair', 'size']
        .reduce(
          (dates, slot) => {
            const slotDates = getSlotDates(slot)
            if (slotDates.length) return mergeTimes(dates, [slotDates[0], slotDates.pop()])
            return dates
          },
          [Infinity, -Infinity]
        )
        .map(t => new Date(t))

      const mainUnit =
        this.$store.getters.mainUnitDimension?.options.find(opt => opt.isDefault) ||
        this.$store.getters.mainUnits[0]
      const pairUnit =
        this.$store.getters.pairUnitDimension?.options.find(opt => opt.isDefault) ||
        this.$store.getters.pairUnits[0]
      const sizeUnit =
        this.$store.getters.sizeUnitDimension?.options.find(opt => opt.isDefault) ||
        this.$store.getters.sizeUnits[0]

      this.$store.dispatch('resetChartQuery', {
        ...this.$store.getters.breakdownDefaults,
        mainUnit: mainUnit?.id,
        pairUnit: pairUnit?.id,
        sizeUnit: sizeUnit?.id,
        time: [dataDates[0].getTime(), dataDates[dataDates.length - 1].getTime()],
        timeScale: 'A',
      })
    },
    updateSelection(dim, value) {
      if (dim === 'breakdowns') {
        this.$emit('update', {
          dim: dim,
          ...value,
        })
      } else {
        this.$emit('update', { dim, value })
      }
    },
    switchMode() {
      const version = this.$route.params.version
      const versionName = this.$route.params.versionName
      const query = location.hash.replace(/.+(?=\?)\??/, '')
      this.$router.push({
        path: `/explorer/${version}/${versionName}/indicator/${this.layout}/${this.lng}?${query}`,
      })
    },
  },
}
</script>

<style lang="stylus">
.plotter-header
  display: block
  border-bottom: 1px solid $gray

  .mobile &
    border-bottom: none
    padding-top: vw(40px)

    +m-breakpoint(landscape)
      padding-top: vw(25px)

    &[layout="embedded"]
      padding-top: vw(20px)

      +m-breakpoint(md)
        padding-top: vh(10px)

  &[layout="embedded"]
    padding-top: vw(30px)

  &[layout="print"]
    border-bottom: none

    .plotter-header__col.left
      max-width: "calc(100vw - %s)" % vw(100px)

  section
    +m-breakpoint(sm md)
      flex-direction: column

    &.meta
      height: 20px
      position: relative

    &.main
      display: block
      grid-template-columns: 250px calc(70vw - 250px)
      grid-template-rows: repeat(4, auto)
      width: 70vw

      +m-breakpoint(lg)
        width: calc(60vw - 250px)
        display: grid

      .mobile &
        width: "calc(100vw - %s)" % vw(40px)

        [layout="embedded"] &
          width: "calc(100vw - %s)" % vw(20px)

      [layout="embedded"] &
      [layout="print"] &
        width: "calc(100vw - %s)" % vw(75px)
        grid-template-columns: 1fr

  .flip-selector
    grid-column: 1
    grid-row: 2 / 6
    display: flex
    justify-content: center
    align-items: center

    [layout="embedded"] &
    [layout="print"] &
      display: none

    +m-breakpoint(lg)
      border-right: 1px solid $gray

  .indicator-selector
    grid-column: 2

    for i in 1 2 3
      &:nth-child({i + 1})
        grid-row: i + 1

    &.faded
      opacity: .3

    .mobile &
      &:nth-last-child(2) .indicator-selector__name
        border-bottom: none

    [layout="embedded"] &
    [layout="print"] &
      grid-column: 1

      .indicator-selector__indicator
        padding-right: 0px
        margin-left: 0px

        .indicator-selector__breakdowns
          width: 100%
          max-width: 90vw

.plotter-header__country-selectors
  grid-column: 2
  grid-row: 5
  display: flex
  justify-content: flex-start
  padding: vw(4px) 0

  .mobile &
    border-top: 1px solid $gray
    flex-wrap: wrap

  [layout="embedded"] &
  [layout="print"] &
    grid-column: 1

  .country-selector
    margin-left: vw(10px)
    min-width: $dropdown-width
    flex-grow: 0

    .mobile &
      flex-grow: 1
      margin-left: vw(5px)
      min-width: unset

    [layout="embedded"] &
    [layout="print"] &
      margin-left: 0px

.plotter-header__country-filter
  display: flex
  align-items: center
  padding-left: 1em

  &.show-full-label
    margin-left: 1em

  .mobile &
    border: none !important
    margin-left: vw(10px)
    padding-left: 0

    &.show-full-label
      display: none

    .switcher
      margin-bottom: auto

    label
      padding-left: 0
      font-size: .55em
      margin-bottom: vw(2px)
      width: 75px
      line-height: 1

  switcher
    margin-left: vw(30px)

  label
    padding-left: 1.5em
    font-size: .7em

    &[fade-out]
      opacity: .5

.plotter-header__meta
  flex: 1
  font-weight: 400

.plotter-header__mode-switcher
  position: absolute
  right: vw(-55px)
  top: vh(20px)

  .mobile &
    right: vw(-8px)
    top: vw(-30px)
    z-index: 1
    display: flex
    flex-direction: row-reverse

    +m-breakpoint(landscape)
      top: vw(-20px)
      right: 0

.plotter-header__reset-chart-btn
.plotter-header__mode-switcher-btn
  color: $green
  cursor: pointer
  display: block
  font-family: "Bernina Sans"
  font-size: .75em
  line-height: 1.2

  .mobile &
    font-size: 0.65em

.plotter-header__mode-switcher-btn
  border: 1px solid $gray
  border-radius: 2em
  padding: vw(10px) vw(30px)

  .mobile &
    padding: vw(7px) vw(24px)
    margin-left: vw(10px)

    +m-breakpoint(landscape)
      padding: vw(5px) vw(16px)

  &:hover
    background-color: rgba($green, 0.2)
    color: $green

  &:active
    background-color: $green
    color: #fff

.plotter-header__reset-chart-btn
  text-align: center
  color: $black
  padding-top: vw(10px + 2rem)

  .mobile &
    padding-top: vw(8px)
    margin-right: vw(2px)

    +m-breakpoint(md)
      padding-top: vw(5px)

  &:hover
    text-decoration: underline
    color: #000

.plotter-header__title
  flex: 1 1 0%
</style>
